import { asFullUSD } from "@silo/utils";
import { ReactNode } from "react";
import { match } from "ts-pattern";
import { isNullable } from "effect/Predicate";
import { ProtocolInstance, protocolMap, USD_DECIMALS } from "@silo/types";
import { ExternalLink } from "../link";
import { Invisible } from "../invisible";
import { classNames } from "../../utils/class-names";

interface DashboardRewardsProps {
  protocolInstance: ProtocolInstance;
  rewardsTokenSymbol: string;
  revenueTokenSymbol: string;
  revenueBalanceUsd: bigint;
  baseRewardsUsd: bigint;
  merklRewardsUsd: bigint | null;
  baseClaim: ReactNode;
  merklClaim: ReactNode;
  revenueClaim: ReactNode;
  detailsLink: string;
  isRevenueProtocol: boolean;
}

const buttonClassName = "block !text-base";

export function DashboardRewards({
  protocolInstance,
  rewardsTokenSymbol,
  revenueTokenSymbol,
  revenueBalanceUsd,
  baseRewardsUsd,
  merklRewardsUsd,
  baseClaim,
  merklClaim,
  revenueClaim,
  detailsLink,
  isRevenueProtocol,
}: DashboardRewardsProps) {
  const protocolRevenueBalanceUsd = isRevenueProtocol ? revenueBalanceUsd : 0n;

  const totalRewardsUsd =
    baseRewardsUsd + (merklRewardsUsd ?? 0n) + protocolRevenueBalanceUsd;
  const hasRewards = totalRewardsUsd > 0n;

  return (
    <Root>
      <Heading protocolInstance={protocolInstance} />

      {hasRewards ? (
        <RewardsPanel
          totalRewardsUsd={totalRewardsUsd}
          rewardsTokenSymbol={rewardsTokenSymbol}
          baseRewardsUsd={baseRewardsUsd}
          merklRewardsUsd={merklRewardsUsd}
          baseClaim={baseClaim}
          merklClaim={merklClaim}
          detailsLink={detailsLink}
          revenueBalanceUsd={revenueBalanceUsd}
          revenueTokenSymbol={revenueTokenSymbol}
          revenueClaim={revenueClaim}
          isRevenueProtocol={isRevenueProtocol}
        />
      ) : (
        <Panel>
          <NoRewards detailsLink={detailsLink} />
        </Panel>
      )}
    </Root>
  );
}

interface RewardsPanel {
  totalRewardsUsd: bigint;
  rewardsTokenSymbol: string;
  revenueTokenSymbol: string;
  revenueBalanceUsd: bigint;
  baseRewardsUsd: bigint;
  merklRewardsUsd: bigint | null;
  baseClaim: ReactNode;
  merklClaim: ReactNode;
  revenueClaim: ReactNode;
  detailsLink: string;
  isRevenueProtocol: boolean;
}

function RewardsPanel({
  totalRewardsUsd,
  rewardsTokenSymbol,
  revenueTokenSymbol,
  baseRewardsUsd,
  merklRewardsUsd,
  revenueBalanceUsd,
  baseClaim,
  merklClaim,
  revenueClaim,
  detailsLink,
  isRevenueProtocol,
}: RewardsPanel) {
  return (
    <Panel>
      <ValueContainer>
        <Label>Total rewards</Label>

        <Value className="text-2xl">
          {asFullUSD({
            value: totalRewardsUsd,
            decimals: USD_DECIMALS,
          })}
        </Value>
      </ValueContainer>

      {isRevenueProtocol && (
        <ValueContainer className="flex lg:hidden">
          <Label className="text-amber-300">{revenueTokenSymbol}</Label>

          <Value className="text-amber-300">
            {asFullUSD({
              value: revenueBalanceUsd,
              decimals: USD_DECIMALS,
            })}
          </Value>

          <div>{revenueClaim}</div>
        </ValueContainer>
      )}

      <ValueContainer className="flex lg:hidden">
        <Label>{rewardsTokenSymbol}</Label>

        <Value>
          {asFullUSD({
            value: baseRewardsUsd,
            decimals: USD_DECIMALS,
          })}
        </Value>

        <div>{baseClaim}</div>
      </ValueContainer>

      <ValueContainer className="flex lg:hidden">
        <Label>Other</Label>

        <Value>
          {isNullable(merklRewardsUsd)
            ? "--"
            : asFullUSD({
                value: merklRewardsUsd,
                decimals: USD_DECIMALS,
              })}
        </Value>

        <div>{merklClaim}</div>
      </ValueContainer>

      <div className="hidden lg:flex">
        <div
          className="grid grid-rows-2 gap-y-2.5 gap-x-5"
          style={{ gridTemplateColumns: "auto auto auto" }}
        >
          {isRevenueProtocol && (
            <>
              <Label className="text-amber-300">{revenueTokenSymbol}</Label>

              <Value className="text-amber-300">
                {asFullUSD({
                  value: revenueBalanceUsd,
                  decimals: USD_DECIMALS,
                })}
              </Value>

              <div>{revenueClaim}</div>
            </>
          )}

          <Label>{rewardsTokenSymbol}</Label>

          <Value>
            {asFullUSD({
              value: baseRewardsUsd,
              decimals: USD_DECIMALS,
            })}
          </Value>

          <div>{baseClaim}</div>

          <Label>Other</Label>

          <Value>
            {isNullable(merklRewardsUsd)
              ? "--"
              : asFullUSD({
                  value: merklRewardsUsd,
                  decimals: USD_DECIMALS,
                })}
          </Value>

          <div>{merklClaim}</div>
        </div>
      </div>

      <div className="flex">
        <ExternalLink
          href={detailsLink}
          size="normal"
          variant="action"
          className={buttonClassName}
        >
          Rewards details
        </ExternalLink>
      </div>
    </Panel>
  );
}

interface NoRewardsProps {
  detailsLink: string;
}

function NoRewards({ detailsLink }: NoRewardsProps) {
  return (
    <div className="flex items-center gap-5">
      <Label>No rewards yet</Label>

      <ExternalLink
        href={detailsLink}
        size="normal"
        variant="action"
        className={buttonClassName}
      >
        Rewards details
      </ExternalLink>
    </div>
  );
}

function PulseDashboardRewards() {
  return (
    <Root className="animate-pulse">
      <Panel className="p-0">
        <Invisible>
          <Heading protocolInstance="mainnetLegacy" />
        </Invisible>
      </Panel>

      <Panel>
        <Invisible>
          <NoRewards detailsLink="#" />
        </Invisible>
      </Panel>
    </Root>
  );
}

DashboardRewards.buttonClassName = buttonClassName;
DashboardRewards.PulseDashboardRewards = PulseDashboardRewards;

function getProtocolLabel(protocolInstance: ProtocolInstance) {
  return match(protocolInstance)
    .with("anvil", () => protocolMap.anvil.label)
    .with("arbitrum", () => protocolMap.arbitrum.label)
    .with("optimism", () => protocolMap.optimism.label)
    .with("base", () => protocolMap.base.label)
    .with("lrtbase", () => protocolMap.lrtbase.label)
    .with("local", () => protocolMap.local.label)
    .with("mainnetLegacy", () => protocolMap.mainnetLegacy.label)
    .with("mainnetLlama", () => "Ethereum · LLAMA edition")
    .with("mainnetGrand", () => protocolMap.mainnetGrand.label)
    .exhaustive();
}

interface LabelProps {
  className?: string;
  children?: ReactNode;
}

function Label({ className, children }: LabelProps) {
  return (
    <span className={classNames("text-base text-shale-200", className)}>
      {children}
    </span>
  );
}

interface ValueProps {
  className?: string;
  children?: ReactNode;
}

function Value({ className, children }: ValueProps) {
  return (
    <span className={classNames("font-mono font-medium text-white", className)}>
      {children}
    </span>
  );
}

interface ValueContainerProps {
  className?: string;
  children?: ReactNode;
}

function ValueContainer({ className, children }: ValueContainerProps) {
  return (
    <span
      className={classNames("flex items-center gap-5 flex-wrap", className)}
    >
      {children}
    </span>
  );
}

interface PanelProps {
  className?: string;
  children?: ReactNode;
}

function Panel({ className, children }: PanelProps) {
  return (
    <span
      className={classNames(
        "flex flex-col p-5 rounded-lg bg-shale-800 gap-7 lg:pr-48",
        className
      )}
    >
      {children}
    </span>
  );
}

interface RootProps {
  className?: string;
  children?: ReactNode;
}

function Root({ className, children }: RootProps) {
  return (
    <span
      className={classNames(
        "flex flex-col items-stretch gap-10 sm:items-start",
        className
      )}
    >
      {children}
    </span>
  );
}

interface HeadingProps {
  protocolInstance: ProtocolInstance;
  className?: string;
}

function Heading({ className, protocolInstance }: HeadingProps) {
  return (
    <h1
      className={classNames(
        "text-lg font-medium text-white sm:text-2xl",
        className
      )}
    >
      My rewards on {getProtocolLabel(protocolInstance)}
    </h1>
  );
}
